import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Chip,
  LinearProgress,
  IconButton,
} from '@mui/material';
import {
  Contacts,
  LocalOffer,
  Visibility,
  DateRange,
  ShowChart,
  BarChart as BarChartIcon,
  EmojiEvents,
  InfoOutlined,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { dashboardAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

/* ─── GetSettled Brand Colors ─── */
const GS = {
  orange: '#FF8C42',
  orangeLight: '#FFF5EF',
  orangeDark: '#E06526',
  teal: '#4A7C8E',
  tealLight: '#E6F2F5',
  tealDark: '#1E3A45',
  amber: '#F0A500',
  textPrimary: '#1E3A45',
  textMuted: '#6B7280',
  textLight: '#9CA3AF',
  border: '#E5E7EB',
  surface: '#F9FAFB',
  white: '#ffffff',
};

const tooltipContentStyle = {
  borderRadius: 12,
  border: `1px solid ${GS.border}`,
  backgroundColor: GS.white,
  padding: '12px 16px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
};

const CustomViewsTooltip = ({ active, payload, moduleName, activeColor }) => {
  if (active && payload?.length) {
    const data = payload[0]?.payload;
    let formattedDate = data?.label;
    if (data?.date) {
      const [y, m, d] = data.date.split('-').map(Number);
      if (y && m && d) {
        const dateObj = new Date(Date.UTC(y, m - 1, d));
        formattedDate = dateObj.toLocaleDateString(undefined, {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        });
      }
    }

    return (
      <div style={tooltipContentStyle}>
        <div style={{ fontWeight: 700, color: GS.textPrimary, marginBottom: 8, fontSize: 13 }}>
          {formattedDate}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <span style={{ color: GS.textMuted }}>{moduleName}:</span>
            <span style={{ fontWeight: 700, color: activeColor }}>
              {(data?.views ?? 0).toLocaleString()}
            </span>
          </div>
          {data?.totalViews !== undefined && data?.totalViews !== data?.views && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <span style={{ color: GS.textLight }}>Total:</span>
              <span style={{ fontWeight: 600, color: GS.textPrimary }}>
                {data.totalViews.toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

CustomViewsTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
  moduleName: PropTypes.string,
  activeColor: PropTypes.string,
};

const pad2 = (n) => String(n).padStart(2, '0');

/** Compute start and end ISO dates for Current Week or Selected Month/Year */
function getUserViewsDateRange(isThisWeek, selectedYear, selectedMonth) {
  const now = new Date();

  if (isThisWeek) {
    const day = now.getDay();
    // Monday as start of week: 0 is Sun, 1 is Mon
    const diffToMon = now.getDate() - day + (day === 0 ? -6 : 1);
    const mon = new Date(now.getFullYear(), now.getMonth(), diffToMon);
    const sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);

    return {
      period: 'week',
      startDate: undefined,
      endDate: undefined,
      label: `This Week (${mon.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${sun.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })})`,
    };
  }

  // Monthly mode (default)
  const yr = Number(selectedYear) || now.getFullYear();
  const mo = Number(selectedMonth !== undefined ? selectedMonth : now.getMonth());
  const lastDay = new Date(yr, mo + 1, 0).getDate();

  const startISO = `${yr}-${pad2(mo + 1)}-01T00:00:00.000Z`;
  const endISO = `${yr}-${pad2(mo + 1)}-${pad2(lastDay)}T23:59:59.999Z`;

  const monthDate = new Date(yr, mo, 1);
  return {
    period: 'month',
    startDate: startISO,
    endDate: endISO,
    label: monthDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
  };
}

const UserViewsChart = ({ defaultPeriod = 'week' }) => {
  const { t, language } = useLanguage();

  const now = useMemo(() => new Date(), []);
  const currentYear = useMemo(() => now.getFullYear(), [now]);
  const currentMonth = useMemo(() => now.getMonth(), [now]);

  // Year options: strictly from 2025 up to current year
  const yearOptions = useMemo(() => {
    const minYear = 2025;
    const maxYear = Math.max(minYear, currentYear);
    const list = [];
    for (let y = minYear; y <= maxYear; y++) {
      list.push(y);
    }
    return list;
  }, [currentYear]);

  // Month names based on current locale
  const monthNames = useMemo(() => {
    const loc = language === 'de' ? 'de-DE' : 'en-US';
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(2025, i, 1);
      return d.toLocaleDateString(loc, { month: 'long' });
    });
  }, [language]);

  // Filter States
  const [module, setModule] = useState('contact'); // 'contact' | 'promotion'
  const [isThisWeek, setIsThisWeek] = useState(defaultPeriod === 'week');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [chartType, setChartType] = useState('bar'); // 'area' | 'bar'

  // Data States
  const [loading, setLoading] = useState(true);
  const [viewsData, setViewsData] = useState(null);

  // Active theme color depending on module
  const activeColor = module === 'promotion' ? GS.teal : GS.orange;
  const activeBgLight = module === 'promotion' ? GS.tealLight : GS.orangeLight;

  // Compute active range
  const dateRange = useMemo(
    () => getUserViewsDateRange(isThisWeek, selectedYear, selectedMonth),
    [isThisWeek, selectedYear, selectedMonth]
  );

  // Fetch API
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {
          period: isThisWeek ? 'week' : (dateRange.period || 'month'),
          module,
        };

        // For month view, pass startDate and endDate
        if (!isThisWeek && dateRange.startDate && dateRange.endDate) {
          params.startDate = dateRange.startDate;
          params.endDate = dateRange.endDate;
        }

        const res = await dashboardAPI.getUserViews(params);
        if (cancelled) return;

        const payload = res?.data?.data ?? res?.data ?? null;
        setViewsData(payload);
      } catch {
        if (!cancelled) {
          setViewsData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [module, isThisWeek, dateRange]);

  // Process chart rows (ensuring strict UTC mapping so days match accurately)
  const chartRows = useMemo(() => {
    const list =
      viewsData?.viewsByDate ??
      viewsData?.viewsByDay ??
      viewsData?.byDate ??
      viewsData?.dailyViews ??
      viewsData?.items ??
      viewsData?.days ??
      (Array.isArray(viewsData) ? viewsData : []);

    if (!Array.isArray(list) || list.length === 0) {
      return [];
    }

    return list.map((item) => {
      const rawDate = item.date || item.day || item.slotCalendarDate || item.label || '';
      let label = rawDate;

      if (rawDate) {
        const cleanDate = String(rawDate).split('T')[0];
        const parts = cleanDate.split('-').map(Number);
        if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
          const [y, m, d] = parts;
          const dateObj = new Date(Date.UTC(y, m - 1, d));
          if (isThisWeek) {
            label = dateObj.toLocaleDateString(undefined, {
              weekday: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            });
          } else {
            label = String(d);
          }
        }
      }

      const totalViews = Number(item.totalViews ?? item.total ?? item.views ?? 0);
      const contactViews = Number(item.contactViews ?? 0);
      const promotionViews = Number(item.promotionViews ?? 0);
      const views =
        module === 'promotion'
          ? (item.promotionViews ?? (module === 'contact' ? contactViews : totalViews))
          : (item.contactViews ?? totalViews);

      return {
        date: rawDate,
        label,
        views: Number(views) || 0,
        totalViews,
        contactViews,
        promotionViews,
      };
    });
  }, [viewsData, module, isThisWeek]);

  // Summary Metrics
  const summaryMetrics = useMemo(() => {
    const total = Number(viewsData?.totalViews ?? viewsData?.total ?? 0);
    const modViews = Number(
      module === 'promotion'
        ? (viewsData?.promotionViews ?? total)
        : (viewsData?.contactViews ?? total)
    );

    const rowCount = chartRows.length || 1;
    const avg = (modViews / rowCount).toFixed(1);

    let peakViews = 0;
    let peakDate = null;
    for (const row of chartRows) {
      if (row.views > peakViews) {
        peakViews = row.views;
        peakDate = row.date;
      }
    }

    return { total, modViews, avg, peakViews, peakDate };
  }, [viewsData, module, chartRows]);

  // Top Items
  const topItems = useMemo(() => {
    const rawItems =
      viewsData?.topItems ??
      viewsData?.topViewed ??
      viewsData?.topContacts ??
      viewsData?.topPromotions ??
      viewsData?.items ??
      [];
    if (!Array.isArray(rawItems)) return [];
    return rawItems.slice(0, 5);
  }, [viewsData]);

  const maxTopViewCount = useMemo(() => {
    if (!topItems.length) return 1;
    return Math.max(...topItems.map((item) => Number(item.viewCount || 0)), 1);
  }, [topItems]);

  const handleThisWeekClick = () => {
    setIsThisWeek(true);
  };

  const handleMonthChange = (e) => {
    setIsThisWeek(false);
    setSelectedMonth(Number(e.target.value));
  };

  const handleYearChange = (e) => {
    setIsThisWeek(false);
    setSelectedYear(Number(e.target.value));
  };

  const cardSx = {
    borderRadius: 3,
    border: `1px solid ${GS.border}`,
    bgcolor: GS.white,
    boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
  };

  return (
    <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, sm: 2.5 }, pb: 2, mb: 0 }}>
      {/* ─── Top Header & Controls ─── */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', lg: 'center' },
          gap: 2,
          mb: 3,
          pb: 2.5,
          borderBottom: `1px solid ${GS.border}`,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: activeBgLight,
                color: activeColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
              }}
            >
              <Visibility sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: GS.textPrimary, letterSpacing: '-0.01em' }}>
              {t('pages.dashboard.views.title')}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: GS.textMuted, mt: 0.5, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
            {t('pages.dashboard.views.subtitle')}
          </Typography>
        </Box>

        {/* ─── Filter Controls Bar (Dropdowns + This Week) ─── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.25,
            width: { xs: '100%', lg: 'auto' },
            justifyContent: { xs: 'flex-start', lg: 'flex-end' },
          }}
        >
          {/* 1. Module Dropdown: Contact and Promotion */}
          <FormControl size="small" sx={{ minWidth: { xs: 140, sm: 175 } }}>
            <InputLabel id="views-module-label">{t('pages.dashboard.views.module')}</InputLabel>
            <Select
              labelId="views-module-label"
              value={module}
              label={t('pages.dashboard.views.module')}
              onChange={(e) => setModule(e.target.value)}
              sx={{
                borderRadius: 2,
                bgcolor: GS.white,
                fontWeight: 600,
                fontSize: '0.8125rem',
                height: 38,
              }}
            >
              <MenuItem value="contact" sx={{ fontSize: '0.8125rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Contacts sx={{ fontSize: 16, color: GS.orange }} />
                {t('pages.dashboard.views.contact')}
              </MenuItem>
              <MenuItem value="promotion" sx={{ fontSize: '0.8125rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalOffer sx={{ fontSize: 16, color: GS.teal }} />
                {t('pages.dashboard.views.promotion')}
              </MenuItem>
            </Select>
          </FormControl>

          {/* 2. "This Week" Button */}
          <Button
            variant={isThisWeek ? 'contained' : 'outlined'}
            size="small"
            onClick={handleThisWeekClick}
            startIcon={<DateRange sx={{ fontSize: 16 }} />}
            sx={{
              height: 38,
              borderRadius: 2,
              px: 1.75,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8125rem',
              whiteSpace: 'nowrap',
              bgcolor: isThisWeek ? activeColor : GS.white,
              color: isThisWeek ? GS.white : GS.textPrimary,
              borderColor: isThisWeek ? activeColor : GS.border,
              boxShadow: isThisWeek ? `0 2px 8px ${activeColor}33` : 'none',
              '&:hover': {
                bgcolor: isThisWeek ? (module === 'promotion' ? GS.tealDark : GS.orangeDark) : GS.surface,
                borderColor: isThisWeek ? activeColor : GS.border,
              },
            }}
          >
            {t('pages.dashboard.views.currentWeek')}
          </Button>

          {/* 3. Month Dropdown */}
          <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 135 } }}>
            <InputLabel id="views-month-label">{t('pages.dashboard.views.month')}</InputLabel>
            <Select
              labelId="views-month-label"
              value={selectedMonth}
              label={t('pages.dashboard.views.month')}
              onChange={handleMonthChange}
              sx={{
                borderRadius: 2,
                bgcolor: GS.white,
                fontWeight: 600,
                fontSize: '0.8125rem',
                height: 38,
                opacity: isThisWeek ? 0.7 : 1,
              }}
            >
              {monthNames.map((name, idx) => (
                <MenuItem key={name} value={idx} sx={{ fontSize: '0.8125rem' }}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 4. Year Dropdown (2025 to Current Year) */}
          <FormControl size="small" sx={{ minWidth: { xs: 90, sm: 95 } }}>
            <InputLabel id="views-year-label">{t('pages.dashboard.views.year')}</InputLabel>
            <Select
              labelId="views-year-label"
              value={selectedYear}
              label={t('pages.dashboard.views.year')}
              onChange={handleYearChange}
              sx={{
                borderRadius: 2,
                bgcolor: GS.white,
                fontWeight: 600,
                fontSize: '0.8125rem',
                height: 38,
                opacity: isThisWeek ? 0.7 : 1,
              }}
            >
              {yearOptions.map((yr) => (
                <MenuItem key={yr} value={yr} sx={{ fontSize: '0.8125rem' }}>
                  {yr}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 5. Chart View Toggle (Area / Bar) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: `1px solid ${GS.border}`,
              borderRadius: 2,
              p: '2px',
              bgcolor: GS.surface,
              height: 38,
              boxSizing: 'border-box',
            }}
          >
            <IconButton
              size="small"
              onClick={() => setChartType('area')}
              sx={{
                borderRadius: 1.5,
                bgcolor: chartType === 'area' ? GS.white : 'transparent',
                color: chartType === 'area' ? activeColor : GS.textMuted,
                boxShadow: chartType === 'area' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                p: '5px',
              }}
              title={t('pages.dashboard.views.chartArea')}
            >
              <ShowChart sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setChartType('bar')}
              sx={{
                borderRadius: 1.5,
                bgcolor: chartType === 'bar' ? GS.white : 'transparent',
                color: chartType === 'bar' ? activeColor : GS.textMuted,
                boxShadow: chartType === 'bar' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                p: '5px',
              }}
              title={t('pages.dashboard.views.chartBar')}
            >
              <BarChartIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* ─── Metric Summary Chips ─── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: GS.surface, border: `1px solid ${GS.border}` }}>
            <Typography variant="caption" sx={{ color: GS.textMuted, fontWeight: 600, display: 'block' }}>
              {module === 'promotion' ? t('pages.dashboard.views.promotionViews') : t('pages.dashboard.views.contactViews')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: activeColor, mt: 0.5 }}>
              {loading ? '—' : summaryMetrics.modViews.toLocaleString()}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: GS.surface, border: `1px solid ${GS.border}` }}>
            <Typography variant="caption" sx={{ color: GS.textMuted, fontWeight: 600, display: 'block' }}>
              {t('pages.dashboard.views.totalViews')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: GS.textPrimary, mt: 0.5 }}>
              {loading ? '—' : summaryMetrics.total.toLocaleString()}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: GS.surface, border: `1px solid ${GS.border}` }}>
            <Typography variant="caption" sx={{ color: GS.textMuted, fontWeight: 600, display: 'block' }}>
              {t('pages.dashboard.views.dailyAverage')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: GS.textPrimary, mt: 0.5 }}>
              {loading ? '—' : `${summaryMetrics.avg}/day`}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: GS.surface, border: `1px solid ${GS.border}` }}>
            <Typography variant="caption" sx={{ color: GS.textMuted, fontWeight: 600, display: 'block' }}>
              {t('pages.dashboard.views.peakViews')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: GS.textPrimary, mt: 0.5 }}>
              {loading ? '—' : summaryMetrics.peakViews.toLocaleString()}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* ─── Main Content Grid: Chart (Left) + Top Performing Items (Right) ─── */}
      <Grid container spacing={2} alignItems="stretch">
        {/* Chart View */}
        <Grid item xs={12} lg={topItems.length > 0 ? 8 : 12} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ position: 'relative', flex: 1, minHeight: 280, width: '100%', display: 'flex', flexDirection: 'column' }}>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                  bgcolor: 'rgba(255,255,255,0.7)',
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={36} sx={{ color: activeColor }} />
              </Box>
            )}

            {!loading && chartRows.length === 0 && (
              <Box
                sx={{
                  height: '100%',
                  minHeight: 260,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: GS.textMuted,
                  gap: 1,
                }}
              >
                <InfoOutlined sx={{ fontSize: 36, color: GS.textLight }} />
                <Typography variant="body2">{t('pages.dashboard.views.noData')}</Typography>
              </Box>
            )}

            {chartRows.length > 0 && (
              <Box sx={{ flex: 1, width: '100%', minHeight: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  {chartType === 'area' ? (
                    <AreaChart data={chartRows} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="userViewsGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={activeColor} stopOpacity={0.35} />
                          <stop offset="95%" stopColor={activeColor} stopOpacity={0.01} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={GS.border} vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 10, fill: GS.textMuted }}
                        stroke={GS.border}
                        height={18}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: GS.textMuted }}
                        stroke={GS.border}
                        allowDecimals={false}
                      />
                      <Tooltip
                        content={
                          <CustomViewsTooltip
                            activeColor={activeColor}
                            moduleName={
                              module === 'promotion'
                                ? t('pages.dashboard.views.promotion')
                                : t('pages.dashboard.views.contact')
                            }
                          />
                        }
                        animationDuration={200}
                      />
                      <Area
                        type="monotone"
                        dataKey="views"
                        stroke={activeColor}
                        strokeWidth={2.5}
                        fillOpacity={1}
                        fill="url(#userViewsGradient)"
                        activeDot={{ r: 6, fill: activeColor, stroke: GS.white, strokeWidth: 2 }}
                      />
                    </AreaChart>
                  ) : (
                    <BarChart data={chartRows} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={GS.border} vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 10, fill: GS.textMuted }}
                        stroke={GS.border}
                        height={18}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: GS.textMuted }}
                        stroke={GS.border}
                        allowDecimals={false}
                      />
                      <Tooltip
                        content={
                          <CustomViewsTooltip
                            activeColor={activeColor}
                            moduleName={
                              module === 'promotion'
                                ? t('pages.dashboard.views.promotion')
                                : t('pages.dashboard.views.contact')
                            }
                          />
                        }
                        cursor={{ fill: `${activeColor}12`, radius: 4 }}
                        animationDuration={200}
                      />
                      <Bar
                        dataKey="views"
                        fill={activeColor}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={32}
                        animationDuration={800}
                      />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Top Items Leaderboard */}
        {topItems.length > 0 && (
          <Grid item xs={12} lg={4} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                bgcolor: GS.surface,
                border: `1px solid ${GS.border}`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmojiEvents sx={{ fontSize: 18, color: GS.amber }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: GS.textPrimary, fontSize: '0.8125rem' }}>
                    {t('pages.dashboard.views.topItems')}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${topItems.length}`}
                  sx={{
                    height: 18,
                    fontSize: '0.675rem',
                    fontWeight: 700,
                    bgcolor: activeBgLight,
                    color: activeColor,
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, justifyContent: 'space-between' }}>
                {topItems.map((item, index) => {
                  const viewCount = Number(item.viewCount || 0);
                  const progressPct = Math.round((viewCount / maxTopViewCount) * 100);

                  const rankColors = [
                    { bg: '#FEF3C7', color: '#D97706' }, // Gold
                    { bg: '#F3F4F6', color: '#4B5563' }, // Silver
                    { bg: '#FFEDD5', color: '#C2410C' }, // Bronze
                  ];
                  const rankStyle = rankColors[index] || { bg: GS.white, color: GS.textMuted };

                  return (
                    <Box
                      key={item.entityId || index}
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: GS.white,
                        border: `1px solid ${GS.border}`,
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, pr: 1 }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: rankStyle.bg,
                              color: rankStyle.color,
                              fontSize: '0.7rem',
                              fontWeight: 800,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: GS.textPrimary,
                              fontSize: '0.78125rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={item.title}
                          >
                            {item.title}
                          </Typography>
                        </Box>

                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: activeColor,
                            fontSize: '0.725rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {viewCount.toLocaleString()} {t('pages.dashboard.views.viewsCount', { count: '' }).trim()}
                        </Typography>
                      </Box>

                      <LinearProgress
                        variant="determinate"
                        value={progressPct}
                        sx={{
                          height: 3,
                          borderRadius: 2,
                          bgcolor: GS.surface,
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 2,
                            bgcolor: activeColor,
                          },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

UserViewsChart.propTypes = {
  defaultPeriod: PropTypes.string,
};

export default UserViewsChart;
